import React, { useEffect, useState } from 'react';
import { useReportService } from '../../services/ReportService';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { Button, Stack, MultiSelect, Text, Divider } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { DateInput } from '@mantine/dates';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { ProductListFragment } from '../../graphql/graphql';
import { BrandingType } from '../../graphql/graphql';
import { useProductService } from '../../services/ProductService';
import z from 'zod';

const validationSchema = z
	.object({
		productIds: z.array(z.string()).min(1, 'At least one product must be selected'),
		signupDateFrom: z.null().or(z.coerce.date()),
		signupDateTo: z.null().or(z.coerce.date()),
	})
	.refine(
		(data) => {
			if (!data.signupDateFrom || !data.signupDateTo) {
				return true;
			}
			return data.signupDateFrom <= data.signupDateTo;
		},
		{
			message: '"Signup Date From" must be before or equal to "Signup Date To"',
			path: ['signupDateTo'],
		},
	);

type HealthProfessionalReportForm = {
	productIds: string[];
	signupDateFrom: Date | null;
	signupDateTo: Date | null;
};

export type HealthProfessionalReportProps = {
	className?: string;
	onReportGenerated: (report: Blob, filenamePrefix: string) => void;
};

export default function HealthProfessionalReport(props: HealthProfessionalReportProps) {
	const { className, onReportGenerated } = props;

	const { isLoading, addLoader, removeLoader } = useLoadTracker();
	const [products, setProducts] = useState<ProductListFragment[]>([]);

	const reportService = useReportService();
	const productService = useProductService();

	useEffect(() => {
		const loader = addLoader();
		const request = productService.getByBrandingType(BrandingType.Ndc);
		request.response
			.then((products) => {
				setProducts(products);
			})
			.catch((err) => {
				NotificationUtils.showError(err as Error, 'Failed to load products');
			})
			.finally(() => {
				removeLoader(loader);
			});
	}, [addLoader, removeLoader, productService]);

	const form = useForm<HealthProfessionalReportForm>({
		initialValues: {
			productIds: [],
			signupDateFrom: null,
			signupDateTo: null,
		},
		validate: schemaResolver(validationSchema),
	});

	const handleSubmit = async (values: HealthProfessionalReportForm) => {
		const loader = addLoader();
		try {
			const report = await reportService.getHealthProfessionalReport({
				productIds: values.productIds,
				signupDateFrom: values.signupDateFrom ?? undefined,
				signupDateTo: values.signupDateTo ?? undefined,
			});
			onReportGenerated(report, 'health_professional_report');
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to generate report');
		} finally {
			removeLoader(loader);
		}
	};

	const productData = products.map((p) => ({
		value: p.id,
		label: p.shortName,
	}));

	return (
		<div className={`ExportOptions ${className ?? ''}`}>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack maw={640}>
					<Text size="sm">
						This report will generate a CSV file containing all health professionals who have signed up for
						NDC courses, including their signup questions and professional details.
					</Text>
					<MultiSelect
						{...form.getInputProps('productIds')}
						data={productData}
						label="NDC Product(s)"
						description="Select one or more NDC products"
						placeholder={isLoading ? 'Loading...' : 'Select products'}
						disabled={isLoading}
						withAsterisk
					/>
					<Divider />
					<Text size="sm">You may optionally specify a date range to filter by the signup date</Text>
					<DateInput
						{...form.getInputProps('signupDateFrom')}
						label="Signup Date From"
						description="Lower date bound for when the user signed up"
						valueFormat="DD-MM-YYYY"
						popoverProps={{ withinPortal: true }}
						disabled={isLoading}
						clearable
					/>
					<DateInput
						{...form.getInputProps('signupDateTo')}
						label="Signup Date To"
						description="Upper bound for when the user signed up"
						valueFormat="DD-MM-YYYY"
						popoverProps={{ withinPortal: true }}
						disabled={isLoading}
						clearable
					/>
					<Divider />
					<Button type="submit" className="flex ac jc gxs" loading={isLoading}>
						<span>Generate Report (CSV)</span>
					</Button>
				</Stack>
			</form>
		</div>
	);
}
